import { get, uniqBy, forEach, findIndex, concat, find, map, toNumber, unionBy } from 'lodash';
import { TSCostCode, TimesheetByHourCode, TimesheetFormikState } from '~/detail/types';
import {
  TimesheetDayCrewMember,
  CostCode,
  CrewMember,
  ScheduleHourType,
  TimeEntryInput,
  Shift,
  Crew,
  TimesheetCostCode,
} from '@bechtel/types';

//TODO: Get unique costcodes from entered timesheets. Maynot be needed. Refactor it to one iteration
const getUniqueCostCodes = (timesheetDayCrewMembers: TimesheetDayCrewMember[]) => {
  const costCodes: CostCode[] = [];

  forEach(timesheetDayCrewMembers, (timesheetDayCrewMember) => {
    const uniqueTimeEntries = uniqBy(timesheetDayCrewMember.timeEntries, (timeEntry) => timeEntry.costCode.id);
    forEach(uniqueTimeEntries, (timeEntry) => {
      if (findIndex(costCodes, (costCode) => timeEntry.costCode.id === costCode.id) === -1) {
        costCodes.push(timeEntry.costCode);
      }
    });
  });
  return costCodes;
};

//To get already entered timesheet
const getTimeEntryByCostCodeHourTypeCrewEmployee = (
  costCodeId: string,
  hourCode: string,
  crewMemberId: string,
  timesheetDayCrewMembers: TimesheetDayCrewMember[]
) => {
  const foundTimesheetDayCrewMember = find(
    timesheetDayCrewMembers,
    (timesheetDayCrewMember) => timesheetDayCrewMember.crewMember.id === crewMemberId
  );
  if (foundTimesheetDayCrewMember) {
    const foundTimeEntry = find(
      foundTimesheetDayCrewMember.timeEntries,
      (timeEntry) => timeEntry.costCode.id === costCodeId && timeEntry.hourCode === hourCode
    );
    return foundTimeEntry;
  }
  return null;
};

//To get Costcodes timesheet data
export const getTimesheetData = (
  pinnedCostCodes: TimesheetCostCode[],
  crewEmployees: CrewMember[],
  hourTypes: ScheduleHourType[],
  timesheetDayCrewMembers: TimesheetDayCrewMember[],
  costCodesOrderedInView: CostCode[]
) => {
  let timesheetCostCodes: TSCostCode[] = [];
  const costCodes = getUniqueCostCodes(timesheetDayCrewMembers);

  let allCostCodes = uniqBy(
    concat(
      map(
        uniqBy(pinnedCostCodes, (pinnedCostCode) => pinnedCostCode.costCode.id),
        (pinnedCode) => pinnedCode.costCode
      ),
      costCodes
    ),
    (costCode) => costCode.id
  );

  //Preserve sort order what is in the view.
  //Server returns CostCodes (TimesheetDayCrewMember) on every save payload on any order.
  if (costCodesOrderedInView && costCodesOrderedInView.length > 0) {
    allCostCodes = unionBy(costCodesOrderedInView, allCostCodes, (costCode) => costCode.id);
  }

  forEach(allCostCodes, (costCode) => {
    let timesheetByHourCodes: TimesheetByHourCode[] = [];
    forEach(hourTypes, (hourType) => {
      let hourCodeTimeEntries: TimeEntryInput[] = [];
      forEach(crewEmployees, (crewEmployee, index) => {
        const foundTimeEntry = getTimeEntryByCostCodeHourTypeCrewEmployee(
          costCode.id,
          hourType.hourCode,
          crewEmployee.id,
          timesheetDayCrewMembers
        );
        const foundTimeEntryInput = foundTimeEntry
          ? {
              id: foundTimeEntry.id,
              hourCode: foundTimeEntry.hourCode,
              costCodeId: foundTimeEntry.costCode.id,
              crewMemberId: crewEmployee.id,
              hours: foundTimeEntry.hours ? foundTimeEntry.hours : '',
              commentId: foundTimeEntry.comment?.id,
              otherCommentDescription: foundTimeEntry.comment?.value,
            }
          : null;
        const timeentry: TimeEntryInput = foundTimeEntryInput
          ? foundTimeEntryInput
          : {
              hourCode: hourType.hourCode,
              hours: '',
              costCodeId: costCode.id,
              crewMemberId: crewEmployee.id,
            };
        hourCodeTimeEntries = concat(hourCodeTimeEntries, timeentry);
      });
      const timesheetByHourCode: TimesheetByHourCode = {
        hourCode: hourType.hourCode,
        timeEntries: hourCodeTimeEntries,
        allowEdit: true,
      };
      timesheetByHourCodes = concat(timesheetByHourCodes, timesheetByHourCode);
    });
    const pinnedTimesheetCostCode: TimesheetCostCode | undefined = find(
      pinnedCostCodes,
      (pinnedCostCode) => pinnedCostCode.costCode.id === costCode.id
    );
    timesheetCostCodes = timesheetCostCodes.concat({
      costCode: costCode,
      hourCodeTimeEntry: timesheetByHourCodes,
      pinnedCostCodeId: pinnedTimesheetCostCode ? pinnedTimesheetCostCode.id : '',
    });
  });
  return { timesheetCostCodes: timesheetCostCodes };
};

export const getTimesheetParsedValues = (data) => {
  const timesheetDay = get(data, 'timesheetByForeman');
  const error = timesheetDay?.error;
  const crewEmployees: CrewMember[] = get(data, 'timesheetByForeman.crewDetails.crewEmployees', []);
  const foreman: CrewMember = get(data, 'timesheetByForeman.crewDetails.foreman', null);
  const generalForeman: CrewMember = get(data, 'timesheetByForeman.crewDetails.generalForeman', null);
  const timesheetDayCrewMembers: TimesheetDayCrewMember[] = get(data, 'timesheetByForeman.crewMembers', []);
  const shift: Shift = get(data, 'timesheetByForeman.crewDetails.shift', null);
  const crew: Crew = get(data, 'timesheetByForeman.crewDetails', null);
  const timesheetId = get(data, 'timesheetByForeman.id', '');
  const crewId = get(data, 'timesheetByForeman.crewDetails.id', '');
  const secheduleHourTypeList = get(data, 'timesheetByForeman.crewDetails.shift.dayShiftSchedule.hourTypes', []);
  const duration = get(data, 'timesheetByForeman.crewDetails.shift.dayShiftSchedule.duration', 0);
  const roundingIncrement = get(data, 'timesheetByForeman.crewDetails.shift.roundingIncrement', 0.0);

  const result = {
    timesheetDay: timesheetDay,
    timesheetGetError: error,
    crewEmployees: crewEmployees,
    foreman: foreman,
    generalForeman: generalForeman,
    timesheetDayCrewMembers: timesheetDayCrewMembers,
    shift: shift,
    crew: crew,
    timesheetId: timesheetId,
    crewId: crewId,
    secheduleHourTypeList: secheduleHourTypeList,
    duration: duration,
    roundingIncrement: roundingIncrement,
  };
  return result;
};

export const getChangedTimeInputs = (
  initialValues: TimesheetFormikState,
  valuesToCompare: TimesheetFormikState
): TimeEntryInput[] => {
  const changedInputs: TimeEntryInput[] = [];
  forEach(valuesToCompare.timesheetCostCodes, (timesheetCostCode, costCodeIndex) => {
    forEach(timesheetCostCode.hourCodeTimeEntry, (hourCodeEntry, hourCodeIndex) => {
      forEach(hourCodeEntry.timeEntries, (timeEntry, timeEntryIndex) => {
        //Check hours, commentId, OtherCommentId
        if (!get(initialValues, `${timesheetCostCode[costCodeIndex]}`, null)) {
          if (
            get(timeEntry, 'hours', 0) !=
              get(
                initialValues,
                `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].hours`,
                0
              ) ||
            get(timeEntry, 'commentId', '') !=
              get(
                initialValues,
                `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].commentId`,
                ''
              ) ||
            get(timeEntry, 'otherCommentDescription', '') !=
              get(
                initialValues,
                `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].otherCommentDescription`,
                ''
              ) ||
            timeEntry.id
          )
            changedInputs.push({ ...timeEntry, hours: timeEntry.hours ? timeEntry.hours : 0 });
        } else {
          //New Costcode row added. Check for hours/comments/othercomments has value
          if (timeEntry.hours || timeEntry.commentId || timeEntry.otherCommentDescription)
            changedInputs.push(timeEntry);
        }
      });
    });
  });
  return changedInputs;
};

export const isDirty = (initialValues: TimesheetFormikState, valuesToCompare: TimesheetFormikState): boolean => {
  let isDirty = false;
  forEach(valuesToCompare.timesheetCostCodes, (timesheetCostCode, costCodeIndex) => {
    forEach(timesheetCostCode.hourCodeTimeEntry, (hourCodeEntry, hourCodeIndex) => {
      forEach(hourCodeEntry.timeEntries, (timeEntry, timeEntryIndex) => {
        //Check hours, commentId, OtherCommentId
        if (
          get(timeEntry, 'hours', 0) !=
            get(
              initialValues,
              `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].hours`,
              0
            ) ||
          get(timeEntry, 'commentId', '') !=
            get(
              initialValues,
              `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].commentId`,
              ''
            ) ||
          get(timeEntry, 'otherCommentDescription', '') !=
            get(
              initialValues,
              `timesheetCostCodes[${costCodeIndex}].hourCodeTimeEntry[${hourCodeIndex}].timeEntries[${timeEntryIndex}].otherCommentDescription`,
              ''
            )
        ) {
          isDirty = true;
          return false;
        }
      });
      if (isDirty) return false;
    });
    if (isDirty) return false;
  });
  return isDirty;
};

export const getTimeEntryInputListFromCrew = (timesheetDayCrewMembers: TimesheetDayCrewMember[]): TimeEntryInput[] => {
  const timeEntryInputs: TimeEntryInput[] = [];
  forEach(timesheetDayCrewMembers, (timesheetDayCrewMember) => {
    forEach(timesheetDayCrewMember.timeEntries, (timeEntry) => {
      const timeEntryInput: TimeEntryInput = {
        id: timeEntry.id,
        hourCode: timeEntry.hourCode,
        commentId: timeEntry.comment ? timeEntry.comment.id : '',
        otherCommentDescription: timeEntry.comment && timeEntry.comment.value ? timeEntry.comment.value : '',
        hours: timeEntry.hours ? timeEntry.hours : 0,
        crewMemberId: timesheetDayCrewMember.crewMember.id,
        costCodeId: timeEntry.costCode.id,
      };
      timeEntryInputs.push(timeEntryInput);
    });
  });
  return timeEntryInputs;
};

export const roundedHoursValue = (hours, roundingIncrement) => {
  const hoursValue = toNumber(hours);
  const modulo = toNumber(hoursValue * 60) % roundingIncrement;
  let roundedHours = hoursValue;
  if (modulo > 0) {
    roundedHours = (toNumber(hoursValue * 60) - modulo) / 60.0;
  }
  if (hoursValue == roundedHours) return hours;
  else return roundedHours;
};
